import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantStoreCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantStoreDto } from "@/features/merchant/api/dto";

/** Met à jour un point de vente (`PUT /MerchantStores/{id}`). */
export const useUpdateMerchantStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchant-stores", "update"],
    mutationFn: ({
      storeId,
      body,
    }: {
      storeId: string;
      body: MerchantStoreDto;
    }) =>
      mutateData({
        url: merchantEndpoints.merchantStores.update(storeId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "PUT",
      }) as Promise<MerchantStoreDto>,
    onSuccess: (_data, variables) => {
      return invalidateAfterMutation(
        queryClient,
        merchantStoreCacheKeys(variables.body.merchantId, variables.storeId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUpdateMerchantStore failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
